
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Lightbulb } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface OpenAiInsightsProps {
  url: string;
}

// This function simulates an OpenAI analysis without requiring an API key
const simulateOpenAiInsights = async (url: string) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Sample insights based on simulated analysis
  const domainType = url.includes('shop') || url.includes('store') ? 'e-commerce' : 
                     url.includes('blog') ? 'content-focused' : 'business';
                     
  const insights = [
    {
      type: "Content",
      insight: `Your website appears to be a ${domainType} site. Consider creating more in-depth content around your primary products/services with at least 1,500 words per key page.`,
    },
    {
      type: "Keywords",
      insight: "Based on your domain name and structure, consider targeting long-tail keywords that include specific location and service combinations.",
    },
    {
      type: "Technical",
      insight: "Ensure all pages have unique meta titles between 50-60 characters and meta descriptions between 140-160 characters for optimal search visibility.",
    },
    {
      type: "Strategy",
      insight: "Consider creating a content calendar focusing on seasonal trends relevant to your industry to capture timely search traffic.",
    }
  ];
  
  return insights;
};

export const OpenAiInsights = ({ url }: OpenAiInsightsProps) => {
  const [insights, setInsights] = useState<Array<{type: string, insight: string}>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getInsights = async () => {
      setLoading(true);
      try {
        const aiInsights = await simulateOpenAiInsights(url);
        setInsights(aiInsights);
      } catch (error) {
        console.error("Error getting AI insights:", error);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      getInsights();
    }
  }, [url]);

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              AI-Powered Insights
            </CardTitle>
            <CardDescription>
              Smart recommendations based on website analysis
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
            AI Generated
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
            <span className="ml-3 text-muted-foreground">Analyzing website data...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {insights.map((item, index) => (
              <div key={index} className="p-3 border border-border rounded-md">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className="mt-1 whitespace-nowrap bg-amber-500/10 text-amber-600 border-amber-200">
                    {item.type}
                  </Badge>
                  <p className="text-sm">{item.insight}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground mt-2">
              * Insights are simulated for demonstration. For real AI-powered analysis using our premium OpenAI integration, please contact us.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OpenAiInsights;
